import React from 'react';
import { Activity, ActivityList, SlapId, FocusedPlace, Board, StickerDesignId } from '../utils/Primitives';
import { EnhancedActivity, EnhancedSlapActivity } from '../utils/useEnhancedSlapActivity';
import PublishActivity from './PublishActivity';
import SlapActivity from './SlapActivity';

interface RecentActivityProps {
  activities: EnhancedActivity[];
  onSlapClick: (slapId: SlapId) => void;
  onStickerClick: (stickerId: StickerDesignId) => void;
  indexingSlaps: Set<SlapId>;
  indexingStickers: Set<StickerDesignId>;
  setFocusOnPlace: (focusedPlace: FocusedPlace) => void;
  boardRef: React.MutableRefObject<Board>;
  onClearActivities: () => void;
  getSlapIndexingStatus?: (slapId: SlapId) => {
    isIndexing: boolean;
    status: string;
    showProgress: boolean;
  };
}

const RecentActivity: React.FC<RecentActivityProps> = ({
  activities,
  onSlapClick,
  onStickerClick,
  indexingSlaps,
  indexingStickers,
  setFocusOnPlace,
  boardRef,
  onClearActivities,
  getSlapIndexingStatus
}) => {
  if (activities.length === 0) {
    return null;
  }

  return (
    <div className="bg-white/90 lg:bg-white/70 backdrop-blur-[2px] rounded-lg shadow-lg p-4 max-w-sm w-full
      lg:relative fixed bottom-4 left-4">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-semibold text-gray-800">Recent Activity</h3>
        <button
          onClick={onClearActivities}
          className="text-gray-500 hover:text-gray-700 transition-colors"
          aria-label="Clear activities"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>
      <div className="space-y-3">
        {activities.map((activity) => {
          switch (activity.type) {
            case 'publish':
              const publishActivity = activity as any; // PublishActivity
              return (
                <PublishActivity
                  key={activity.id}
                  stickerId={publishActivity.stickerId}
                  stickerName={publishActivity.stickerName}
                  imageCID={publishActivity.imageCID}
                  timestamp={activity.timestamp}
                  boardRef={boardRef}
                  onStickerClick={onStickerClick}
                  isIndexing={indexingStickers.has(publishActivity.stickerId)}
                />
              );
            case 'slap':
              const slapActivity = activity as EnhancedSlapActivity;
              const indexingStatus = getSlapIndexingStatus 
                ? getSlapIndexingStatus(slapActivity.slapId)
                : { isIndexing: indexingSlaps.has(slapActivity.slapId), status: '', showProgress: false };
              
              return (
                <SlapActivity
                  key={activity.id}
                  slapId={slapActivity.slapId}
                  placeId={slapActivity.placeId}
                  stickerId={slapActivity.stickerId}
                  size={slapActivity.size}
                  timestamp={activity.timestamp}
                  onSlapClick={onSlapClick}
                  isIndexing={indexingStatus.isIndexing}
                  indexingStatus={indexingStatus.status}
                  showProgress={indexingStatus.showProgress}
                  resourceStatus={slapActivity.resourceStatus}
                  setFocusOnPlace={setFocusOnPlace}
                  boardRef={boardRef}
                  imageCID={slapActivity.imageCID}
                />
              );
            default:
              return null;
          }
        })}
      </div>
    </div>
  );
};

export default RecentActivity;
